import { MapPin, Mail, Phone, Clock, ArrowUpRight, ShieldCheck, Users, Search, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { HYDERABAD_ADDRESS } from "@/lib/data";
import WhyVittaniti from "@/components/WhyVittaniti";
import Seo from "@/components/Seo";

const SERVICE_DETAILS = [
    {
        id: "audit",
        tagline: "From Compliance to Credibility",
        title: "Audit & Assurance",
        intro:
            "Build trust with every stakeholder. We go beyond verifying numbers; we provide the clarity you need to navigate growth and mitigate risk — whether you are preparing for an investor audit, scaling your operations, or strengthening internal controls.",
        whatYouGet: [
            ["Statutory & Internal Audits", "A deep-dive analysis that highlights operational inefficiencies, not just compliance gaps."],
            ["Risk-Focused Reviews", "Proactive identification of internal-control weaknesses before they become liabilities."],
            ["Compliance Assurance", "Staying ahead of regulatory changes in both Indian and US-reporting environments."],
        ],
        cta: "Learn how we audit for your growth",
    },
    {
        id: "taxation",
        tagline: "From Filing to Strategy",
        title: "Taxation Advisory Services",
        intro:
            "Tax compliance should be a strategic advantage, not a hurdle. We manage the intricacies of your tax lifecycle — from planning to dispute resolution — so you can focus on your core business goals while remaining fully compliant.",
        whatYouGet: [
            ["Strategic Tax Planning", "Forward-thinking approaches to minimize your effective tax rate within the bounds of the law."],
            ["End-to-End Compliance", "Full management of federal, state and local tax filings, eliminating the stress of deadlines."],
            ["Dispute Resolution & Advocacy", "Expert representation during audits and appeals to protect your interests."],
        ],
        cta: "Speak to a tax strategist",
    },
    {
        id: "advisory",
        tagline: "From Data to Decision",
        title: "Business Advisory",
        intro:
            "Turn your financial data into a growth engine. We act as your fractional finance department — helping you anticipate cash-flow needs, assess business health and make high-stakes decisions with absolute confidence.",
        whatYouGet: [
            ["Proactive Forecasting", "Scenario planning that helps you prepare for the next 6, 12 and 24 months."],
            ["Operational Insights", "Actionable dashboards that turn complex data into clear, easy-to-read business metrics."],
            ["Valuation & Strategy", "Expert support for fundraising, M&A and long-term capital allocation."],
        ],
        cta: "Learn how we help you scale",
    },
    {
        id: "bookkeeping",
        tagline: "From Keeping Records to Business Clarity",
        title: "Accounting & Bookkeeping",
        intro:
            "Meticulous records. Real-time clarity. Zero friction. We eliminate the administrative burden of finance while ensuring your books are always audit-ready — leveraging automation and a strict senior-review process.",
        whatYouGet: [
            ["Real-Time Accuracy", "Daily reconciliation and categorization so your financial pulse is always current."],
            ["Streamlined AP/AR", "Optimized cash-flow management that ensures you get paid faster and manage expenses smarter."],
            ["Financial Statement Integrity", "Month-end and year-end closes that meet international reporting standards."],
        ],
        cta: "See our technology-first approach",
    },
    {
        id: "setup",
        tagline: "From Complexity to Launch",
        title: "Business Setup / Entity Registration",
        intro:
            "We handle the paperwork, you build the business. Navigating incorporation, tax registrations and compliance is a bottleneck for new founders. Our seamless turnkey setup ensures your business is fully compliant from Day 1.",
        whatYouGet: [
            ["End-to-End Entity Registration", "We manage the entire lifecycle of your business incorporation — from name reservation to formation documents."],
            ["Regulatory Onboarding", "Rapid procurement of essential tax IDs, business registrations and operating permits to keep your launch on schedule."],
            ["Compliance Foundation", "We set up your internal compliance calendars so you never miss a federal, state or local filing deadline."],
        ],
        cta: "Let us launch your business",
    },
];

const TECH_STACK = ["QuickBooks", "Xero", "NetSuite", "Gusto", "Bill.com"];

const ONBOARDING_STEPS = [
    {
        icon: Search,
        day: "Day 1",
        title: "Discovery",
        body: "A 30-minute call to map your current processes, pain points and the outcomes you want.",
    },
    {
        icon: Users,
        day: "Day 2",
        title: "Matching",
        body: "We identify the senior-led pod best suited to your industry, software and reporting standards.",
    },
    {
        icon: Wrench,
        day: "Day 3–5",
        title: "Onboarding",
        body: "Secure access provisioning, system integration and a phased data migration — zero downtime for your team.",
    },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <Seo
                path="/about"
                title="About Us — Founder-led shared services from Hyderabad"
                description="Vittaniti is a founder-led shared-services partner from Hyderabad, India. Big-4 audit discipline (PwC, KPMG) and senior-reviewed delivery for SMEs across the US and Europe."
                keywords="about Vittaniti, founder-led bookkeeping, Big-4 audit experience, Hyderabad shared services, offshore CPA team"
            />
            {/* About Vittaniti — headline only */}
            <section className="container-page pt-16 md:pt-24 pb-12" data-testid="about-hero">
                <div className="flex items-center gap-3 mb-6">
                    <span className="ornament w-12" />
                    <p className="overline">About Vittaniti</p>
                    <span aria-hidden="true" className="eyebrow-dot" />
                </div>
                <h1 className="display text-5xl md:text-6xl lg:text-7xl text-brand-ink max-w-4xl">
                    A decade of quiet, <span className="italic text-brand-amber">careful</span> books<span className="brand-dot">.</span>
                </h1>
            </section>

            {/* Mission / Vision */}
            <section className="section container-page" data-testid="about-mission-vision">
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="card-3d p-9 md:p-11">
                        <p className="overline mb-4 text-brand-amber">Mission</p>
                        <h2 className="font-serif text-3xl text-brand-ink leading-tight">
                            Bring clarity to every line of your business — quietly, accurately, on time.
                        </h2>
                    </div>
                    <div className="card-3d p-9 md:p-11">
                        <p className="overline mb-4 text-brand-amber">Vision</p>
                        <h2 className="font-serif text-3xl text-brand-ink leading-tight">
                            To redefine how businesses build and manage global teams — by making skilled
                            talent, operational excellence and trusted partnerships accessible across borders.
                        </h2>
                    </div>
                </div>
            </section>

            {/* Service detail — strategic outcome-focused blocks */}
            <section className="section container-page" data-testid="about-services-detail">
                <div className="max-w-3xl mb-14">
                    <p className="overline mb-3">What we deliver</p>
                    <h2 className="display text-4xl md:text-5xl lg:text-6xl text-brand-ink leading-[1.05]">
                        Services, framed by <span className="italic text-brand-amber">outcome</span><span className="brand-dot">.</span>
                    </h2>
                    <p className="mt-5 text-brand-ink/70 leading-relaxed">
                        Five core practices that move you from compliance to confidence — each one
                        designed around a real business outcome, not just a list of tasks.
                    </p>
                </div>

                <div className="space-y-6">
                    {SERVICE_DETAILS.map((s, i) => (
                        <article
                            key={s.id}
                            data-testid={`about-service-${s.id}`}
                            className="card-3d p-7 md:p-10 grid lg:grid-cols-12 gap-8 animate-fade-up"
                            style={{ animationDelay: `${i * 70}ms` }}
                        >
                            <div className="lg:col-span-5">
                                <p className="overline text-brand-amber">{s.tagline}</p>
                                <h3 className="display text-3xl md:text-4xl text-brand-ink mt-3 leading-tight">
                                    {s.title}<span className="brand-dot">.</span>
                                </h3>
                                <p className="mt-5 text-sm text-brand-ink/75 leading-relaxed">
                                    {s.intro}
                                </p>
                                <Link
                                    to="/contact"
                                    className="btn-ghost mt-6 inline-flex"
                                    data-testid={`about-service-cta-${s.id}`}
                                >
                                    {s.cta} <ArrowUpRight size={16} />
                                </Link>
                            </div>
                            <div className="lg:col-span-7">
                                <p className="overline mb-4">What you get</p>
                                <ul className="space-y-4">
                                    {s.whatYouGet.map(([title, body]) => (
                                        <li key={title} className="flex gap-3">
                                            <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-[#E53935] shrink-0" />
                                            <div>
                                                <p className="text-sm font-semibold text-brand-ink">{title}</p>
                                                <p className="text-sm text-brand-ink/70 leading-relaxed mt-0.5">{body}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* Vittaniti Standard — review hierarchy callout */}
            <section className="container-page pb-8" data-testid="about-review-standard">
                <div
                    className="card-3d p-10 md:p-14 grid lg:grid-cols-12 gap-8 items-center"
                    style={{ backgroundColor: "#FFF8E8", borderColor: "#F2D9A1" }}
                >
                    <div className="lg:col-span-2 grid place-items-center">
                        <span className="grid place-items-center w-20 h-20 rounded-2xl bg-brand-ink text-brand-amber">
                            <ShieldCheck size={36} strokeWidth={1.4} />
                        </span>
                    </div>
                    <div className="lg:col-span-10">
                        <p className="overline mb-3 text-brand-amber">The Vittaniti Standard</p>
                        <h3 className="font-serif text-2xl md:text-3xl text-brand-ink leading-tight">
                            Every return and financial report undergoes a mandatory{" "}
                            <span className="italic">three-tier review process</span>, starting with
                            a senior-level Certified Accountant sign-off before it ever hits your inbox.
                        </h3>
                    </div>
                </div>
            </section>

            {/* Tech stack we support */}
            <section className="container-page section" data-testid="about-tech-stack">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
                    <div className="lg:col-span-7">
                        <p className="overline mb-3">The tech stack we support</p>
                        <h2 className="display text-4xl md:text-5xl text-brand-ink leading-[1.05]">
                            Experts in the tools you use <span className="italic text-brand-amber">every day</span><span className="brand-dot">.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-brand-ink/70 leading-relaxed">
                            We don&apos;t just work on your books — we plug into your digital ecosystem
                            so books, payroll, AP and reporting all stay in sync.
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {TECH_STACK.map((t) => (
                        <div
                            key={t}
                            data-testid={`tech-${t.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                            className="card-3d p-6 grid place-items-center text-center"
                        >
                            <span className="font-serif text-xl text-brand-ink">{t}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Ready to start — onboarding timeline */}
            <section className="container-page section" data-testid="about-onboarding">
                <div className="max-w-3xl mb-12">
                    <p className="overline mb-3">Ready to start?</p>
                    <h2 className="display text-4xl md:text-5xl text-brand-ink leading-[1.05]">
                        Here&apos;s the <span className="italic text-brand-amber">first step</span><span className="brand-dot">.</span>
                    </h2>
                    <p className="mt-5 text-brand-ink/70 leading-relaxed">
                        Transitioning is easier than you think. We conduct a 30-minute discovery
                        call to map your current processes, followed by a secure, phased data-migration plan —
                        zero downtime for your team.
                    </p>
                </div>
                <ol className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="onboarding-steps">
                    {ONBOARDING_STEPS.map((step, i) => {
                        const Icon = step.icon;
                        return (
                            <li
                                key={step.day}
                                className="card-3d p-7 md:p-8 flex flex-col gap-5 relative animate-fade-up"
                                style={{ animationDelay: `${i * 90}ms` }}
                            >
                                <span className="absolute top-6 right-6 font-serif text-5xl text-brand-creamLight leading-none select-none">
                                    0{i + 1}
                                </span>
                                <span className="grid place-items-center w-12 h-12 rounded-xl bg-brand-creamLight text-brand-ink">
                                    <Icon size={22} strokeWidth={1.6} />
                                </span>
                                <div>
                                    <p className="overline text-brand-amber">{step.day}</p>
                                    <h3 className="font-serif text-2xl text-brand-ink mt-1 leading-tight">{step.title}</h3>
                                    <p className="text-sm text-brand-ink/70 leading-relaxed mt-3">{step.body}</p>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </section>

            {/* Why Vittaniti — for US Bookkeeping */}
            <WhyVittaniti
                testid="why-vittaniti-us"
                kicker="Why Vittaniti — for US Bookkeeping"
                title={<>Built for founders who&apos;d <span className="italic text-brand-amber">rather not</span> think about bookkeeping.</>}
                intro="Big-4 audit discipline meets US GAAP fluency. The result: audit-ready books, every month — without your team feeling tax season."
                cards={WhyVittaniti.US_BOOKKEEPING_CARDS}
            />

            {/* Why Vittaniti — for Remote Teams */}
            <WhyVittaniti
                testid="why-vittaniti-remote"
                kicker="Why Vittaniti — for Remote Teams"
                cards={WhyVittaniti.REMOTE_CARDS}
            />

            {/* Contact strip — navy */}
            <section className="relative isolate" data-testid="about-contact-strip">
                <div className="absolute inset-0 -z-10" style={{ backgroundColor: "#0F1B2E" }} />
                <div className="container-page py-20 md:py-24">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        <div className="lg:col-span-5">
                            <p className="overline text-brand-amber mb-3">Get in touch</p>
                            <h2 className="display text-4xl md:text-5xl text-white leading-[1.05]">
                                Have a conversation with us.
                            </h2>
                            <p className="mt-5 text-white/75 leading-relaxed max-w-md">
                                Our partners read every message and respond within one business day —
                                usually faster. No bots, no junior reps.
                            </p>
                            <Link to="/contact" className="btn-accent mt-8" data-testid="about-contact-cta">
                                Book a free consultation <ArrowUpRight size={16} />
                            </Link>
                        </div>

                        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
                            <InfoTile icon={MapPin} label="Visit us">
                                {HYDERABAD_ADDRESS.line1},<br />
                                {HYDERABAD_ADDRESS.line2},<br />
                                {HYDERABAD_ADDRESS.city}, {HYDERABAD_ADDRESS.country}
                            </InfoTile>
                            <InfoTile icon={Phone} label="WhatsApp">
                                <a href={HYDERABAD_ADDRESS.whatsapp} target="_blank" rel="noreferrer"
                                   className="link-underline" data-testid="about-phone">
                                    {HYDERABAD_ADDRESS.phone}
                                </a>
                            </InfoTile>
                            <InfoTile icon={Mail} label="Email">
                                <a href={`mailto:${HYDERABAD_ADDRESS.email}`}
                                   className="link-underline" data-testid="about-email">
                                    {HYDERABAD_ADDRESS.email}
                                </a>
                            </InfoTile>
                            <InfoTile icon={Clock} label="Hours">
                                {HYDERABAD_ADDRESS.hours}
                            </InfoTile>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function InfoTile({ icon: Icon, label, children }) {
    return (
        <div className="rounded-2xl border border-white/10 p-6"
             style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-white/5 text-brand-amber mb-4">
                <Icon size={18} strokeWidth={1.6} />
            </span>
            <p className="overline text-white/55 mb-2">{label}</p>
            <div className="text-white/85 text-sm leading-relaxed">{children}</div>
        </div>
    );
}
